"use client";

import { Cutting } from "../../portal/components/products/CuttingInfo";
import { useEffect, useState } from "react";
import useProductCuttingModal from "../hooks/useProductCuttingModal";
import useCurrentUser from "../../auth/hooks/useCurrentUser";
import ProductCutting from "./ProductCutting";
import { useTranslations } from "next-intl";
import Button from "../../theme/components/Button";
import AddToCartButton from "../../theme/components/AddToCartButton";
import useAddCartProduct from "../../portal/hooks/useAddCartProduct";
import toast from "react-hot-toast";
import NewCartProductConfirmationToast from "../../portal/components/cart/NewCartProductConfirmationToast";

export default function ProductConfiguration({ product }: { product: any }) {
  const [currentCutting, setCurrentCutting] = useState<Cutting | null>(null);
  const { currentUser } = useCurrentUser();
  const t = useTranslations();
  const { addCartProduct } = useAddCartProduct(currentUser?.currentClient?._id);

  useEffect(() => {
    // Find the cutting configuration for the current article number, then set as a default
    const cuttingConfiguration =
      currentUser?.currentClient?.cuttingConfigurations?.find(
        (c) => c.itemNumber === product.sku,
      );
    if (cuttingConfiguration) {
      setCurrentCutting(cuttingConfiguration.cutting);
    }
  }, [currentUser, product]);

  // TODO: Favorite List Button
  const isBuyable = true;
  const currentClientType = "PRIVATE";
  const isAdministrator = (user: any) => true;
  const showFavorites =
    (isBuyable && currentClientType === "PRIVATE") ||
    (isAdministrator(currentUser) &&
      ["PATIENT", "INSTITUTION"].includes(currentClientType));

  const cuttingRange = product.publicare?.cutting;
  const { openProductCuttingModal, ProductCuttingModal } =
    useProductCuttingModal({
      cutting: currentCutting,
      cuttingRange,
      onCuttingChange: setCurrentCutting,
    });

  const showAddToCart = product?.publicare?.isOnlyForMedProfessionals
    ? currentUser?.self?.isInstitution || !currentUser
    : true;

  if (!currentUser?.currentClient)
    return (
      <Button className="mt-5" appearance="primary" href="/login">
        {t("login_to_order")}
      </Button>
    );

  if (!showAddToCart) return null;

  const onAddToCart = async (quantity: number) => {
    const configuration = currentCutting
      ? [
          {
            key: "cutting",
            value: JSON.stringify(currentCutting),
          },
        ]
      : [];
    console.log("Adding to cart", {
      quantity,
      configuration,
    });
    const orderPosition = await addCartProduct({
      product,
      configuration,
      quantity,
    });
    const orderId = orderPosition?.data?.addCartProduct?.order?._id;
    toast.custom((toastProps) => (
      <NewCartProductConfirmationToast
        product={product}
        quantity={quantity}
        orderId={orderId}
        clientId={currentUser?.currentClient?._id}
        {...toastProps}
      />
    ));
  };

  return (
    <div className="mt-4">
      <span className="fs-7 fw-bold">
        {t("order_for", { name: currentUser?.currentClient?.name })}
      </span>
      {cuttingRange && (
        <ProductCutting
          cutting={currentCutting}
          onClick={openProductCuttingModal}
        />
      )}
      <div className="d-flex flex-column gap-3">
        <AddToCartButton size="lg" onAddToCart={onAddToCart}>
          {t("add_to_cart")}
        </AddToCartButton>
        {ProductCuttingModal}
        {showFavorites && (
          <Button appearance="secondary" icon="favorite">
            {t("add_to_list")}
          </Button>
        )}
      </div>
    </div>
  );
}
